import { useState, useRef, useEffect, useCallback } from "react";
import { useCategories } from "../api/swr/useCategories";
import { useTiers } from "../api/swr/useTiers";
import { Button } from "~/atoms/button";
import { TierModal } from "../organisms/TierModal";
import HeadingPage from "../molecules/HeadingPage";
import { CenterSpinner } from "../atoms/spinner";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { getChartData } from "../helpers/chart";
import CategoryBlock from "../organisms/CategoryBlock";
import { useUserContext } from "../hooks/useUserContext";
import { PlatformRole, DocumentVersionStatus } from "../config/types";
import { useDocumentVersions } from "../api/v2/useDocumentVersions";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { Info } from "lucide-react";
import { DocumentMetadataSheet } from "~/molecules/DocumentMetadataSheet";
import { ManageCategoriesButton } from "~/molecules/ManageCategoriesButton";
import { TagsModal } from "~/organisms/TagsModal";

const statuses = [DocumentVersionStatus.published];

const Chart = () => {
  const { data: categories } = useCategories();
  const { data: tiers } = useTiers();
  const { data: documentVersions, mutate: mutateDocumentVersions } =
    useDocumentVersions({ statuses });
  const { user } = useUserContext();
  const { t } = useTranslation();

  const canManageFramework =
    user.role === PlatformRole.superAdmin ||
    user.role === PlatformRole.coordinator;

  const handleReorder = useCallback(() => {
    void mutateDocumentVersions();
  }, [mutateDocumentVersions]);

  const [filterCategory, setFilterCategory] = useState<number>(-1);
  const [selectedId, setSelectedId] = useState<number>();

  const containerRef = useRef<HTMLDivElement>(null);
  const documentRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = scrollContainerRef.current;
    const bar = stickyScrollRef.current;
    const track = scrollbarTrackRef.current;
    if (!target || !bar || !track) return;

    const maxScroll = (el: HTMLElement) =>
      Math.max(0, el.scrollWidth - el.clientWidth);

    // Sync scroll position proportionally between two elements
    const syncScroll = (from: HTMLElement, to: HTMLElement) => {
      const fromMax = maxScroll(from);
      const toMax = maxScroll(to);
      if (fromMax === 0 || toMax === 0) return;
      const want = (from.scrollLeft / fromMax) * toMax;
      // 1px threshold prevents micro-adjustments that cause jitter
      if (Math.abs(to.scrollLeft - want) > 1) to.scrollLeft = want;
    };

    const update = () => {
      const rect = target.getBoundingClientRect();
      track.style.width = `${target.scrollWidth}px`;
      bar.style.left = `${rect.left}px`;
      bar.style.width = `${rect.width}px`;
      // 1px tolerance accounts for subpixel rounding differences
      const hasOverflow = target.scrollWidth > target.clientWidth + 1;
      bar.classList.toggle("hidden", !hasOverflow);
      syncScroll(target, bar);
    };

    const onBarScroll = () => syncScroll(bar, target);
    const onTargetScroll = () => {
      syncScroll(target, bar);
    };

    bar.addEventListener("scroll", onBarScroll, { passive: true });
    target.addEventListener("scroll", onTargetScroll, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(target);
    update();

    return () => {
      bar.removeEventListener("scroll", onBarScroll);
      target.removeEventListener("scroll", onTargetScroll);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
      ro.disconnect();
    };
    // Re-run when data loads to recalculate overflow and show/hide scrollbar
  }, [categories, tiers]);

  const onChangeView = (value: string) => {
    setFilterCategory(parseInt(value));
  };

  if (!documentVersions || !categories || !tiers) {
    return <CenterSpinner withText />;
  }

  const chartData = getChartData(documentVersions);
  const selectedVersion = selectedId
    ? chartData.documentVersionMap.get(selectedId)
    : undefined;

  const preFilteredCategories =
    user.role === PlatformRole.reader || user.role === PlatformRole.contributor
      ? categories.filter((d) => user.categories?.some((ud) => ud.id === d.id))
      : categories;

  const filteredCategories =
    filterCategory === -1
      ? preFilteredCategories
      : preFilteredCategories.filter((d) => d.id === filterCategory);

  const categoryOptions = [
    { value: "-1", label: t("filter.placeholder") },
    ...preFilteredCategories.map((d) => ({
      value: d.id.toString(),
      label: d.title,
    })),
  ];

  return (
    <>
      <div className="container flex py-7">
        <HeadingPage title={t("chart.header")} />
        <div className="flex flex-1 items-center justify-center">
          <div className="w-72">
            <ControlledCombobox
              options={categoryOptions}
              value={filterCategory.toString()}
              onChange={onChangeView}
            />
          </div>
        </div>
        <div className="items-centers flex gap-6">
          <div className="flex gap-2">
            {user.role !== PlatformRole.reader && (
              <TagsModal
                trigger={
                  <Button variant="outline">
                    {t("modal.tags.manageTags")}
                  </Button>
                }
              />
            )}
            {canManageFramework && (
              <>
                <ManageCategoriesButton />
                <TierModal
                  trigger={
                    <Button variant="outline">
                      {`${t("commons.buttons.create")} ${t("commons.document.tier")}`}
                    </Button>
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="relative flex overflow-x-auto pb-7"
      >
        <div className="sticky left-0 z-20 flex w-64 shrink-0 flex-col bg-gray-100 pl-7">
          <div className="h-9" />
          {tiers.map(({ id, title, description }, i) => (
            <div
              className={cn(
                "flex h-44 flex-col justify-center pl-4",
                i % 2 !== 0 ? "bg-gray-50" : "bg-gray-100",
              )}
              key={id}
            >
              <div className="flex items-center gap-2 pr-4">
                <h3 className="line-clamp-2 flex-1 font-medium wrap-break-word">
                  {title}
                </h3>
                {canManageFramework && (
                  <TierModal
                    tierId={id}
                    trigger={
                      <Button variant="ghost" size="icon">
                        <Info className="h-5 w-5 opacity-50" />
                      </Button>
                    }
                  />
                )}
              </div>
              <div className="text-sm text-gray-500">{description}</div>
            </div>
          ))}
        </div>

        <div className="relative flex pr-7" ref={containerRef}>
          {filteredCategories.map(({ id, title }) => (
            <CategoryBlock
              key={id}
              id={id}
              title={title}
              tiers={tiers}
              categoryMap={chartData.categoryMap}
              documentVersionMap={chartData.documentVersionMap}
              documentRefsMap={documentRefsMap}
              containerRef={containerRef}
              setSelectedId={setSelectedId}
              canManage={canManageFramework}
              onReorder={handleReorder}
            />
          ))}
        </div>
      </div>

      <div
        ref={stickyScrollRef}
        className="fixed bottom-0 z-50 hidden h-4 overflow-x-auto overflow-y-hidden"
      >
        <div ref={scrollbarTrackRef} className="h-px" />
      </div>

      {selectedVersion && (
        <DocumentMetadataSheet
          documentId={selectedVersion.document.id}
          documentVersionId={selectedVersion.id}
          open={true}
          setOpen={() => setSelectedId(undefined)}
          onSave={mutateDocumentVersions}
        />
      )}
    </>
  );
};

export default Chart;
